import type { ApiError } from "./api";

export type UploadErrorContext = {
  error: ApiError;
  fileName: string;
  fileSize: number;
  siteAddress: string;
};

export type AiPreparationContext = {
  siteAddress: string;
  spaFallback: boolean;
};

const formatBytes = (bytes: number) => {
  if (bytes < 1024) return `${bytes} Б`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} КБ`;
  return `${(bytes / 1024 / 1024).toFixed(1)} МБ`;
};

export const buildAiErrorPrompt = ({ error, fileName, fileSize, siteAddress }: UploadErrorContext) => {
  const details = error.details;
  const lines = [
    "Помоги подготовить мой сайт к загрузке в Gagarin Hoster.",
    "",
    "Контекст:",
    "- Хостинг принимает ZIP со статическим сайтом: HTML, CSS, JavaScript, изображения и шрифты.",
    "- В корне ZIP должен быть index.html (или единственная общая папка с index.html).",
    "- Нельзя включать .git, .env, серверные исполняемые файлы и небезопасные пути.",
    `- Сайт: ${siteAddress}`,
    `- Архив: ${fileName}`,
    `- Размер архива: ${formatBytes(fileSize)}`,
    "",
    "Ошибка сервера:",
    `- Код: ${error.code}`,
    `- HTTP-статус: ${error.status}`,
    `- Сообщение: ${error.message}`,
  ];

  if (details?.title) lines.push(`- Тип проблемы: ${details.title}`);
  if (details?.offendingPath) lines.push(`- Проблемный путь: ${details.offendingPath}`);
  if (details?.explanation) lines.push(`- Почему загрузка остановлена: ${details.explanation}`);
  if (details?.suggestion) lines.push(`- Рекомендация сервиса: ${details.suggestion}`);
  if (details?.limits && Object.keys(details.limits).length) {
    lines.push("- Лимиты и значения:");
    for (const [key, value] of Object.entries(details.limits)) lines.push(`  - ${key}: ${value}`);
  }
  if (details?.steps?.length) {
    lines.push("- Предложенные шаги:");
    details.steps.forEach((step, index) => lines.push(`  ${index + 1}. ${step}`));
  }
  if (details?.expectedStructure?.length) {
    lines.push("- Ожидаемая структура ZIP:", ...details.expectedStructure.map((item) => `  - ${item}`));
  }
  if (error.requestId) lines.push(`- ID ошибки для журнала сервера: ${error.requestId}`);

  lines.push(
    "",
    "Что нужно сделать:",
    "1. Изучи структуру моего проекта и найди готовые файлы сайта.",
    "2. Исправь причину ошибки, не удаляя нужные файлы сайта и не меняя его внешний вид.",
    "3. Подготовь отдельный корректный ZIP для публикации.",
    "4. Проверь, что index.html находится в корне ZIP, а все пути к CSS, JS и изображениям работают.",
    "5. Кратко перечисли, что было исправлено и какой архив нужно загрузить.",
  );

  return lines.join("\n");
};

export const buildAiPreparationPrompt = ({ siteAddress, spaFallback }: AiPreparationContext) => [
  "Подготовь мой сайт к публикации в Gagarin Hoster.",
  "",
  "Параметры публикации:",
  `- Будущий адрес: ${siteAddress}`,
  `- Режим SPA fallback: ${spaFallback ? "включён" : "выключен"}`,
  "- Максимальный размер ZIP: 50 МБ.",
  "- В корне ZIP обязательно должен находиться index.html.",
  "",
  "Требования хостинга:",
  "1. Разрешены готовые клиентские файлы: HTML, CSS, JavaScript, JSON, изображения, иконки, видео, аудио и веб-шрифты.",
  "2. Запрещены .git, .env, .htaccess, приватные ключи, node_modules, кэш, отчёты тестов и служебные файлы редакторов.",
  "3. Запрещены серверные и исполняемые файлы: PHP, Python, shell-скрипты, EXE, DLL, JAR и подобные.",
  "4. Все пути внутри ZIP должны быть относительными и не должны содержать переходы через .., абсолютные пути или символические ссылки.",
  "5. Архив должен быть обычным ZIP без пароля и шифрования.",
  "6. Файлы сайта после распаковки должны занимать не более разрешённого лимита; тяжёлые изображения нужно оптимизировать.",
  "7. Ссылки на локальные CSS, JavaScript, изображения и шрифты должны работать после публикации на отдельном домене.",
  "8. Если это проект с процессом сборки, сначала выполни production-сборку и используй содержимое папки dist, build, public или out.",
  "",
  "Ожидаемая структура готового ZIP:",
  "index.html",
  "styles.css или assets/styles.css",
  "script.js или assets/script.js",
  "assets/...",
  "",
  "Что нужно сделать:",
  "1. Изучи структуру моего проекта и определи папку готовой production-сборки.",
  "2. Проверь index.html и все пути к локальным ресурсам.",
  "3. Удали из копии для публикации только служебные и запрещённые файлы, не меняя исходный проект и внешний вид сайта.",
  "4. При необходимости собери проект и исправь пути для работы на отдельном домене.",
  "5. Создай отдельный ZIP, где index.html расположен в корне.",
  "6. Проверь ZIP: распакуй его во временную папку и открой index.html, убедись в отсутствии .git, .env и node_modules.",
  "7. Сообщи точный путь к готовому ZIP и кратко перечисли выполненные проверки.",
].join("\n");
